// src/components/Dashboard.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { parseAssetModel, getBatterySlaveIds, slaveIdToHexPrefix } from '../utils/assetConfig';
import { normalizeRequestHex } from '../utils/modbusDecoder';
import { extractLiveModbus } from '../utils/telemetrySource';
import { fetchLatestSummary } from '../utils/energyApi';
import { API_BASE } from '../utils/api';
import {
  formatPower, formatTime, formatDiesel, formatCO2,
  calculateDieselSavedByTime, calculateCO2Saved
} from '../utils/energyFormat';
import './Dashboard.css';
import SelectorChips from './dashboard/SelectorChips';
import { deviceHealth, aggregateHealth, HEALTH_LABEL } from '../utils/deviceHealth';
import FleetHealthDonut from './dashboard/FleetHealthDonut';
import StatCard from './dashboard/StatCard';
import { canAccessGroups } from '../utils/userDevices';


function Dashboard({ authData, devices }) {
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [availableBatteries, setAvailableBatteries] = useState([]);
  const [selectedBatteryIds, setSelectedBatteryIds] = useState([]);
  const [batteryCache, setBatteryCache] = useState({});
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingBatteries, setLoadingBatteries] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(new Set());
  const [lastUpdated, setLastUpdated] = useState(null);
  const [, setNowTick] = useState(0); // forces relative-time refresh

  // Fetch groups on mount for Role 2 (admin)
  useEffect(() => {
    const fetchGroupsOnMount = async () => {
      if (authData?.role !== 2 || !authData?.token) return;
      setLoadingGroups(true);
      setError(null);
      try {
        const url = `${API_BASE}/api/admin/getGroups?api_access_token=${authData.token}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const fetchedGroups = data.groups || [];
        setGroups(fetchedGroups);
        if (fetchedGroups.length > 0) {
          // Default to the first group
          setSelectedGroupIds([fetchedGroups[0]._id]);
        }
      } catch (err) {
        setError('Failed to load groups');
        console.error(err);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroupsOnMount();
  }, [authData]);

  // Users without group access get their devices via the `devices` prop.
  useEffect(() => {
    if (canAccessGroups(authData?.role)) return;   // role 2 uses the group fetch below
    const userDevices = devices || [];
    setAvailableBatteries(userDevices);
    setSelectedBatteryIds(userDevices.map(d => d.deviceId));
  }, [devices, authData]);

  // Re-render every 30s so the "updated Xm ago" label stays current.
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Fetch batteries in parallel for selected groups (Role 2)
  const fetchVehiclesForGroups = useCallback(async (groupIds) => {
    if (!authData?.token || groupIds.length === 0) {
      setAvailableBatteries([]);
      setSelectedBatteryIds([]);
      return;
    }
    setLoadingBatteries(true);
    setError(null);
    try {
      const allVehicles = [];
      await Promise.all(
        groupIds.map(async (groupId) => {
          const url = `${API_BASE}/api/admin/getGroupVehicles?groupId=${groupId}&api_access_token=${authData.token}`;
          const res = await fetch(url);
          if (res.ok) {
            const data = await res.json();
            if (data.vehicles) {
              allVehicles.push(...data.vehicles);
            }
          }
        })
      );

      // Deduplicate by deviceId
      const uniqueVehicles = [];
      const seen = new Set();
      allVehicles.forEach((v) => {
        if (!seen.has(v.deviceId)) {
          seen.add(v.deviceId);
          uniqueVehicles.push(v);
        }
      });

      setAvailableBatteries(uniqueVehicles);
      setSelectedBatteryIds(uniqueVehicles.map((v) => v.deviceId));
    } catch (err) {
      setError('Failed to fetch batteries for selected groups');
      console.error(err);
    } finally {
      setLoadingBatteries(false);
    }
  }, [authData]);

  useEffect(() => {
    if (authData?.role === 2) {
      fetchVehiclesForGroups(selectedGroupIds);
    }
  }, [selectedGroupIds, authData, fetchVehiclesForGroups]);

  // Fetch status and summaries for selected batteries (filling cache)
  const fetchMetricsForBatteries = useCallback(async (batteryList, force = false) => {
    if (!authData?.token || batteryList.length === 0) return;

    // Skip batteries whose fetch is already in flight so overlapping calls
    // (e.g. selection changing again before the previous fetch settles)
    // don't race each other or duplicate network requests.
    const toFetch = force
      ? batteryList
      : batteryList.filter((battery) => !inFlightRef.current.has(battery.deviceId));
    if (toFetch.length === 0) return;

    toFetch.forEach((battery) => inFlightRef.current.add(battery.deviceId));
    setLoadingMetrics(true);
    setError(null);

    try {
      const results = {};
      await Promise.all(
        toFetch.map(async (battery) => {
          const deviceId = battery.deviceId;

          // Calculate configured packs
          const assetConfig = parseAssetModel(battery.carProfile?.model);
          const slaveIds = getBatterySlaveIds(assetConfig);
          const totalPacks = slaveIds.length;

          let rs485modbus = null;
          let activeCount = 0;

          // 1. Fetch live status
          try {
            const statusUrl = `${API_BASE}/api/admin/getDeviceStatus?api_access_token=${authData.token}&deviceId=${deviceId}`;
            const statusRes = await fetch(statusUrl);
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              rs485modbus = extractLiveModbus(statusData);
            }
          } catch (statusErr) {
            console.warn(`Failed to fetch status for ${deviceId}:`, statusErr);
          }

          if (rs485modbus) {
            slaveIds.forEach((slaveId) => {
              const prefix = slaveIdToHexPrefix(slaveId);
              const hasData = Object.keys(rs485modbus).some((rawKey) => {
                const norm = normalizeRequestHex(rawKey);
                return norm && norm.toUpperCase().startsWith(prefix);
              });
              if (hasData) {
                activeCount++;
              }
            });
          }


          // 2. Fetch latest summary
          let summary = null;
          try {
            summary = await fetchLatestSummary(authData, battery._id || deviceId);
          } catch (summaryErr) {
            console.warn(`Failed to fetch summary for ${deviceId}:`, summaryErr);
          }

          results[deviceId] = {
            loading: false,
            error: null,
            status: rs485modbus ? 'online' : 'offline',
            summary,
            batteryCount: totalPacks,
            activeCount
          };
        })
      );

      setBatteryCache((prev) => ({ ...prev, ...results }));
    } catch (err) {
      setError('Failed to fetch battery metrics');
      console.error(err);
    } finally {
      toFetch.forEach((battery) => inFlightRef.current.delete(battery.deviceId));
      setLoadingMetrics(false);
      setLastUpdated(Date.now());
    }
  }, [authData]);

  // Load metrics when selection or list changes
  useEffect(() => {
    const selectedBatteriesList = availableBatteries.filter((b) =>
      selectedBatteryIds.includes(b.deviceId)
    );
    // Find only those selected batteries that are NOT yet cached
    const uncachedList = selectedBatteriesList.filter(
      (b) => !batteryCache[b.deviceId]
    );

    if (uncachedList.length > 0) {
      fetchMetricsForBatteries(uncachedList);
    }
  }, [selectedBatteryIds, availableBatteries, batteryCache, fetchMetricsForBatteries]);

  // Trigger force refresh
  const handleRefresh = () => {
    const selectedBatteriesList = availableBatteries.filter((b) =>
      selectedBatteryIds.includes(b.deviceId)
    );
    fetchMetricsForBatteries(selectedBatteriesList, true);
  };

  // Group selection handlers
  const toggleGroup = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleSelectAllGroups = () => {
    setSelectedGroupIds(groups.map((g) => g._id));
  };

  const handleClearAllGroups = () => {
    setSelectedGroupIds([]);
  };

  // Battery selection handlers
  const toggleBattery = (deviceId) => {
    setSelectedBatteryIds((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAllBatteries = () => {
    setSelectedBatteryIds(availableBatteries.map((b) => b.deviceId));
  };

  const handleClearAllBatteries = () => {
    setSelectedBatteryIds([]);
  };

  // Aggregated KPIs calculation
  const aggregatedKpis = () => {
    let totalPacks = 0;
    let activePacks = 0;
    let totalBackupHours = 0;
    let totalPowerWh = 0;

    selectedBatteryIds.forEach((deviceId) => {
      const data = batteryCache[deviceId];
      if (data) {
        totalPacks += data.batteryCount || 0;
        activePacks += data.activeCount || 0;
        if (data.summary) {
          totalPowerWh += (data.summary.cum_fc || 0) * 1000;
          totalBackupHours += data.summary.cum_wrk || 0;
        }
      }
    });

    const dieselSaved = calculateDieselSavedByTime(totalBackupHours);
    const co2Saved = calculateCO2Saved(dieselSaved);

    return {
      totalPacks,
      activePacks,
      inactivePacks: Math.max(0, totalPacks - activePacks),
      totalBackupHours,
      totalPowerWh,
      dieselSaved,
      co2Saved
    };
  };

  const kpis = aggregatedKpis();
  const activeSelectionCount = selectedBatteryIds.length;

  const groupItems = groups.map((g) => ({ id: g._id, name: g.groupName }));

  const deviceItems = availableBatteries.map((b) => {
    const cache = batteryCache[b.deviceId];
    const health = cache
      ? deviceHealth(cache.activeCount, cache.batteryCount, cache.status)
      : null;
    const dotColor = health === 'online'
      ? 'var(--ok)'
      : health === 'degraded'
        ? 'var(--warn)'
        : health === 'offline'
          ? 'var(--down)'
          : 'var(--faint)';
    return {
      id: b.deviceId,
      name: b.vehicleId || b.deviceId,
      meta: cache ? `${cache.activeCount}/${cache.batteryCount}` : undefined,
      dotColor,
    };
  });

  const healthRows = availableBatteries
    .filter((b) => selectedBatteryIds.includes(b.deviceId))
    .map((b) => {
      const cache = batteryCache[b.deviceId];
      return {
        activeCount: cache?.activeCount || 0,
        totalPacks: cache?.batteryCount || 0,
        status: cache?.status,
      };
    });
  const health = aggregateHealth(healthRows);

  const relativeUpdated = (() => {
    if (!lastUpdated) return 'Not loaded yet';
    const secs = Math.round((Date.now() - lastUpdated) / 1000);
    if (secs < 45) return 'Updated just now';
    const mins = Math.round(secs / 60);
    if (mins < 60) return `Updated ${mins}m ago`;
    const hrs = Math.round(mins / 60);
    return `Updated ${hrs}h ago`;
  })();

  return (
    <div className="historic-data-view dashboard-view">
      <div className="dashboard-header-row">
        <div>
          <h2>System Dashboard</h2>
          <p className="subtitle">Overall summary metrics and interactive device selection</p>
        </div>
        <div className="dashboard-header-actions">
          <span className="freshness"><span className="freshness-dot" />{relativeUpdated}</span>
          <button
            onClick={handleRefresh}
            disabled={loadingMetrics || activeSelectionCount === 0}
            className="compare-btn primary refresh-dash-btn"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={loadingMetrics ? { animation: 'spin 1.5s linear infinite' } : undefined}>
              <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
            </svg>
            {loadingMetrics ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Interactive Selection Filters Panel */}
      <div className="dashboard-filters-panel">
        {authData?.role === 2 && (
          <SelectorChips
            label="Groups"
            items={groupItems}
            selectedIds={selectedGroupIds}
            onToggle={toggleGroup}
            onSelectAll={handleSelectAllGroups}
            onClearAll={handleClearAllGroups}
            loading={loadingGroups}
            emptyHint="No groups available for your account."
          />
        )}

        <SelectorChips
          label="Devices"
          items={deviceItems}
          selectedIds={selectedBatteryIds}
          onToggle={toggleBattery}
          onSelectAll={handleSelectAllBatteries}
          onClearAll={handleClearAllBatteries}
          loading={loadingBatteries}
          emptyHint={canAccessGroups(authData?.role)
            ? 'Select at least one group above to list its devices.'
            : 'No devices are assigned to your account.'}
        />
      </div>

      {/* Aggregated KPI Cards */}
      {selectedBatteryIds.length === 0 ? (
        <div className="no-data">
          <p>Select at least one battery device to display aggregated metrics.</p>
        </div>
      ) : (
        <>
          <div className="dashboard-hero">
            <FleetHealthDonut
              onlinePacks={health.onlinePacks}
              offlinePacks={health.offlinePacks}
              totalPacks={health.totalPacks}
              onlineDevices={health.onlineDevices}
              degradedDevices={health.degradedDevices}
              offlineDevices={health.offlineDevices}
            />
            <div className="stat-grid">
              <StatCard
                label="Backup delivered" color="var(--accent)"
                value={formatTime(kpis.totalBackupHours)}
                sub="Lifetime generator-offset runtime"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
              />
              <StatCard
                label="Energy provided" color="var(--warn)"
                value={formatPower(kpis.totalPowerWh)}
                sub="Aggregated battery energy output"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>}
              />
              <StatCard
                label="Diesel saved" color="#2dd4bf"
                value={formatDiesel(kpis.dieselSaved)}
                sub="Estimated generator fuel offset"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
              />
              <StatCard
                label="CO₂ avoided" color="var(--ok)"
                value={formatCO2(kpis.co2Saved)}
                sub="Estimated greenhouse-gas offset"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/></svg>}
              />
            </div>
          </div>

          {/* Individual Battery Details Breakdown Table */}
          <div className="results dashboard-breakdown-section">
            <div className="results-header">
              <h3>Individual Battery Breakdown</h3>
            </div>
            <div className="table-container">
              <table className="historic-table">
                <thead>
                  <tr>
                    <th>Vehicle ID</th>
                    <th>Device ID</th>
                    <th>Model</th>
                    <th>Status</th>
                    <th className="num">Packs</th>
                    <th className="num">Backup Time</th>
                    <th className="num">Power Provided</th>
                    <th className="num">Diesel Saved</th>
                    <th className="num">CO₂ Saved</th>
                  </tr>
                </thead>
                <tbody>
                  {availableBatteries
                    .filter((battery) => selectedBatteryIds.includes(battery.deviceId))
                    .map((battery) => {
                      const data = batteryCache[battery.deviceId];
                      const isLoadingRow = data?.loading || !data;

                      const backupHours = data?.summary?.cum_wrk || 0;
                      const powerWh = (data?.summary?.cum_fc || 0) * 1000;
                      const dieselSaved = calculateDieselSavedByTime(backupHours);
                      const co2Saved = calculateCO2Saved(dieselSaved);

                      return (
                        <tr key={battery.deviceId}>
                          <td>{battery.vehicleId || 'N/A'}</td>
                          <td><code>{battery.deviceId}</code></td>
                          <td>{battery.carProfile?.model || 'Unknown'}</td>
                          <td>
                            {isLoadingRow ? (
                              <span className="row-status-loading">Loading…</span>
                            ) : (() => {
                              const h = deviceHealth(data.activeCount, data.batteryCount, data.status);
                              return (
                                <span className={`status-pill ${h}`}>
                                  <span className="status-pill-dot" />
                                  {HEALTH_LABEL[h]}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="num data-cell">
                            {isLoadingRow ? '—' : `${data.activeCount}/${data.batteryCount}`}
                          </td>
                          <td className="num data-cell">{isLoadingRow ? '—' : formatTime(backupHours)}</td>
                          <td className="num data-cell">{isLoadingRow ? '—' : formatPower(powerWh)}</td>
                          <td className="num data-cell">{isLoadingRow ? '—' : formatDiesel(dieselSaved)}</td>
                          <td className="num data-cell">{isLoadingRow ? '—' : formatCO2(co2Saved)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
